import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import { apiJson } from "./api";

export type User = {
  id: string;
  email: string;
  username: string; // [profiles+follows] public handle
  display_name: string;
  roles: string[];
  created_at: string;
};

export type TokenPair = { access_token: string; refresh_token: string };

// Response to /auth/register. `dev_otp` is only ever populated by a local backend running with
// DEV_EXPOSE_OTP — it lets the signup form prefill the code when there is no mail server to
// deliver it. Always undefined against any normal deployment.
export type SignupPending = { message: string; email: string; dev_otp?: string | null };

type AuthState = {
  user: User | null;
  accessToken: string | null;
  loading: boolean; // true while restoring a persisted session on first load
  expired: boolean; // true when the session ended on its own (idle / refresh failure)
  login: (email: string, password: string) => Promise<void>;
  // Start signup: emails a verification code and creates NO account yet. Returns void; call
  // verifySignup with the emailed code to actually create the account and sign in.
  register: (
    email: string,
    password: string,
    confirmPassword: string,
    username: string,
  ) => Promise<SignupPending>;
  // Confirm the emailed code → creates the account, then establishes the session.
  verifySignup: (email: string, otp: string) => Promise<void>;
  // Establish a session from an already-issued TokenPair (e.g. the Google Sign-In callback page,
  // which gets its tokens from POST /auth/google/session rather than a login form). Resolves with
  // the loaded profile so the caller can inspect it (the Google button reads created_at to tell a
  // first-time sign-up apart from a repeat login).
  establishSession: (tokens: TokenPair) => Promise<User>;
  logout: () => void;
  dismissExpired: () => void; // clear the "session expired" flag (e.g. after the user acts)
  refreshUser: () => Promise<void>; // [profiles+follows] re-pull /auth/me (e.g. after a username change)
};

const AuthContext = createContext<AuthState | null>(null);

// The refresh token is persisted here so a reload keeps the user signed in.
const REFRESH_KEY = "sp_refresh_token";
// Timestamp (ms) of the user's last interaction — drives the idle auto-logout.
const ACTIVITY_KEY = "sp_last_activity";

// Auto-logout after this much inactivity (no mouse/key/scroll/touch).
const IDLE_TIMEOUT_MS = 6 * 60 * 60 * 1000; // 6 hours
// Silently refresh the 15-min access token well before it expires, so an active
// session keeps working without a reload (and survives until the idle timeout).
const REFRESH_INTERVAL_MS = 12 * 60 * 1000; // 12 minutes

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [, setRefreshTokenState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);

  const setRefreshToken = useCallback((token: string | null) => {
    setRefreshTokenState(token);
    if (typeof window !== "undefined") {
      if (token) window.localStorage.setItem(REFRESH_KEY, token);
      else window.localStorage.removeItem(REFRESH_KEY);
    }
  }, []);

  // Store a fresh token pair and load the profile — the shared tail of login/verifySignup.
  const establishSession = useCallback(
    async (tokens: TokenPair) => {
      setAccessToken(tokens.access_token);
      setRefreshToken(tokens.refresh_token);
      setExpired(false);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
      }
      const me = await apiJson<User>("/auth/me", {}, tokens.access_token);
      setUser(me);
      return me;
    },
    [setRefreshToken],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      const tokens = await apiJson<TokenPair>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      await establishSession(tokens);
    },
    [establishSession],
  );

  const register = useCallback(
    async (email: string, password: string, confirmPassword: string, username: string) => {
      // Emails a verification code; no account exists until verifySignup succeeds.
      return await apiJson<SignupPending>("/auth/register", {
        method: "POST",
        body: JSON.stringify({
          email,
          password,
          confirm_password: confirmPassword,
          username,
        }),
      });
    },
    [],
  );

  const verifySignup = useCallback(
    async (email: string, otp: string) => {
      const tokens = await apiJson<TokenPair>("/auth/verify-signup", {
        method: "POST",
        body: JSON.stringify({ email, otp }),
      });
      await establishSession(tokens);
    },
    [establishSession],
  );

  const clearSession = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(ACTIVITY_KEY);
      // Drop the cached API key so the next user on this browser doesn't inherit it.
      window.localStorage.removeItem("sp_api_key");
      // Reset per-device UI preferences on logout.
      window.localStorage.removeItem("sp-theme");
      window.localStorage.removeItem("sp-sidebar-collapsed");
    }
  }, [setRefreshToken]);

  // Explicit user-initiated logout — no "session expired" popup.
  const logout = useCallback(() => {
    setExpired(false);
    clearSession();
  }, [clearSession]);

  // Session ended on its own (idle timeout or a failed refresh) — flag it so the
  // global modal can inform the user and send them to log in again.
  const expireSession = useCallback(() => {
    clearSession();
    setExpired(true);
  }, [clearSession]);

  const dismissExpired = useCallback(() => setExpired(false), []);

  // [profiles+follows] Re-fetch the signed-in user (e.g. after editing username/display name on the
  // profile page) so the nav + links reflect the change without a full reload. Best-effort.
  const refreshUser = useCallback(async () => {
    if (!accessToken) return;
    try {
      setUser(await apiJson<User>("/auth/me", {}, accessToken));
    } catch {
      /* keep the current user on a transient failure */
    }
  }, [accessToken]);

  // Swap the short-lived access token for a fresh one using the persisted
  // refresh token. Ends the session if the refresh token is gone/expired.
  const silentRefresh = useCallback(async () => {
    const stored =
      typeof window !== "undefined" ? window.localStorage.getItem(REFRESH_KEY) : null;
    if (!stored) return;
    try {
      const tokens = await apiJson<TokenPair>("/auth/refresh", {
        method: "POST",
        body: JSON.stringify({ refresh_token: stored }),
      });
      setAccessToken(tokens.access_token);
      setRefreshToken(tokens.refresh_token);
    } catch {
      expireSession();
    }
  }, [setRefreshToken, expireSession]);

  // On first load, restore the session from the persisted refresh token —
  // unless the last activity is older than the idle window (then it's expired).
  useEffect(() => {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem(REFRESH_KEY) : null;
    const last =
      typeof window !== "undefined" ? Number(window.localStorage.getItem(ACTIVITY_KEY) || 0) : 0;

    if (!stored || (last && Date.now() - last > IDLE_TIMEOUT_MS)) {
      if (stored) setRefreshToken(null); // idle too long → drop the stale session
      if (typeof window !== "undefined") window.localStorage.removeItem(ACTIVITY_KEY);
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const tokens = await apiJson<TokenPair>("/auth/refresh", {
          method: "POST",
          body: JSON.stringify({ refresh_token: stored }),
        });
        const me = await apiJson<User>("/auth/me", {}, tokens.access_token);
        if (cancelled) return;
        setAccessToken(tokens.access_token);
        setRefreshToken(tokens.refresh_token);
        if (typeof window !== "undefined") {
          window.localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
        }
        setUser(me);
      } catch {
        if (!cancelled) setRefreshToken(null); // invalid/expired → clear it
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setRefreshToken]);

  // While signed in: keep the access token fresh on an interval.
  useEffect(() => {
    if (!user) return;
    const id = setInterval(silentRefresh, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [user, silentRefresh]);

  // While signed in: idle auto-logout. Any activity records a timestamp and
  // re-arms a timer; after IDLE_TIMEOUT_MS of silence the session ends.
  useEffect(() => {
    if (!user || typeof window === "undefined") return;

    let timer: ReturnType<typeof setTimeout>;
    let lastWrite = 0;
    const markActivity = () => window.localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    const arm = () => {
      clearTimeout(timer);
      timer = setTimeout(expireSession, IDLE_TIMEOUT_MS);
    };
    const onActivity = () => {
      const now = Date.now();
      if (now - lastWrite > 5000) {
        lastWrite = now;
        markActivity(); // throttled so mousemove doesn't hammer localStorage
      }
      arm();
    };

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"] as const;
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    markActivity();
    arm();
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      clearTimeout(timer);
    };
  }, [user, expireSession]);

  // Cross-tab sync: if another tab signs out (or idles out), follow it here.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === REFRESH_KEY && e.newValue === null) {
        setUser(null);
        setAccessToken(null);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        loading,
        expired,
        login,
        register,
        verifySignup,
        establishSession,
        logout,
        dismissExpired,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
